/**
 * KnowledgeBaseEditor — Task 3.10
 * Super Admin UI for editing pranic healing intro content stored in MongoDB.
 *
 * Layout: section list on left (9 rows), edit panel on right.
 * Language toggle: EN / TA / HI — switches the content being edited.
 * Textarea: plain text only, max 2000 chars, character count shown.
 * Version + updatedBy shown below textarea — accountability, not decoration.
 * Save → PUT /api/superadmin/knowledge-base/:section → clears backend cache.
 *
 * Mobile: section list collapses above editor on narrow screens (≤768px).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getKnowledgeBase, updateKbSection, getKbHistory } from '../utils/api';

// Must match SECTIONS array in backend/models/KnowledgeBase.js — update both together if sections change
const SECTIONS = [
  { key: 'whatIsPranicHealing', label: 'What Is Pranic Healing' },
  { key: 'howItWorks',          label: 'How It Works' },
  { key: 'benefits',            label: 'Benefits' },
  { key: 'whatToExpect',        label: 'What To Expect' },
  { key: 'howManySessions',     label: 'How Many Sessions' },
  { key: 'safety',              label: 'Safety' },
  { key: 'whoCanBenefit',       label: 'Who Can Benefit' },
  { key: 'isItReligious',       label: 'Is It Religious' },
  { key: 'faq',                 label: 'FAQ' },
];

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'ta', label: 'TA' },
  { code: 'hi', label: 'HI' },
];

const MAX_CHARS = 2000;

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  wrap:          { display: 'flex', gap: '0', fontFamily: "'Raleway', sans-serif", minHeight: '500px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
  sidebar:       { width: '220px', flexShrink: 0, borderRight: '1px solid #e0e0e0', background: '#fafafa', overflowY: 'auto' },
  sidebarHead:   { padding: '14px 16px', fontSize: '10px', fontWeight: '700', color: '#888', letterSpacing: '1px', textTransform: 'uppercase', borderBottom: '1px solid #e8e8e8' },
  sectionBtn:    { width: '100%', textAlign: 'left', padding: '11px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#444', borderBottom: '1px solid #f0f0f0', transition: 'background 0.15s' },
  sectionActive: { background: '#1a6b5a', color: '#fff' },
  editor:        { flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto' },
  editorHead:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' },
  title:         { fontSize: '15px', fontWeight: '700', color: '#1a1a2e' },
  langToggle:    { display: 'flex', gap: '4px' },
  langBtn:       { padding: '5px 14px', border: '1px solid #ddd', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontSize: '11px', fontWeight: '700', color: '#666', transition: 'all 0.15s' },
  langActive:    { background: '#1a6b5a', color: '#fff', border: '1px solid #1a6b5a' },
  textarea:      { width: '100%', minHeight: '200px', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', fontFamily: "'Raleway', sans-serif", resize: 'vertical', lineHeight: '1.6', boxSizing: 'border-box', outline: 'none' },
  charRow:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' },
  charCount:     { fontSize: '11px', color: '#aaa' },
  charOver:      { fontSize: '11px', color: '#e74c3c', fontWeight: '700' },
  meta:          { fontSize: '11px', color: '#aaa', fontStyle: 'italic' },
  saveBtn:       { padding: '10px 28px', background: '#1a6b5a', color: '#fff', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', cursor: 'pointer', alignSelf: 'flex-start', minHeight: '44px' },
  saveBtnDis:    { opacity: 0.5, cursor: 'not-allowed' },
  histBtn:       { padding: '10px 20px', background: '#fff', color: '#555', border: '1px solid #ddd', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '600', cursor: 'pointer', alignSelf: 'flex-start', minHeight: '44px', marginLeft: '8px' },
  histPanel:     { borderTop: '1px solid #e8e8e8', paddingTop: '14px', marginTop: '4px' },
  histHead:      { fontSize: '11px', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' },
  histEntry:     { padding: '10px 14px', borderRadius: '6px', background: '#f7f8f7', marginBottom: '8px', fontSize: '12px' },
  histMeta:      { color: '#888', marginBottom: '4px' },
  histContent:   { color: '#333', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  histEmpty:     { color: '#aaa', fontSize: '12px', fontStyle: 'italic' },
  toast:         { padding: '10px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' },
  toastOk:       { background: '#eafaf1', color: '#1a6b5a', border: '1px solid #a9dfbf' },
  toastErr:      { background: '#fdf2f2', color: '#c0392b', border: '1px solid #f5c6cb' },
  empty:         { color: '#aaa', fontSize: '11px', fontStyle: 'italic' },
};

// ── Component ─────────────────────────────────────────────────────────────────

const KnowledgeBaseEditor = () => {
  const [kbData,        setKbData]        = useState({});  // section → [{ language, content, version, updatedBy, updatedAt }]
  const [loading,       setLoading]       = useState(true);
  const [activeSection, setActiveSection] = useState(SECTIONS[0].key);
  const [activeLang,    setActiveLang]    = useState('en');
  const [editContent,   setEditContent]   = useState('');
  const [saving,        setSaving]        = useState(false);
  const [toast,         setToast]         = useState(null); // { type: 'ok'|'err', message }
  const [showHistory,   setShowHistory]   = useState(false);
  const [history,       setHistory]       = useState([]);
  const [histLoading,   setHistLoading]   = useState(false);

  // Load all KB data on mount
  const loadKb = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getKnowledgeBase();
      setKbData(res.data.data);
    } catch (err) {
      setToast({ type: 'err', message: 'Failed to load knowledge base. Please refresh.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKb(); }, [loadKb]);

  // When section or language changes, populate textarea and reset history panel
  useEffect(() => {
    const records = kbData[activeSection] || [];
    const match   = records.find(r => r.language === activeLang);
    setEditContent(match ? match.content : '');
    setShowHistory(false);
    setHistory([]);
  }, [activeSection, activeLang, kbData]);

  async function handleToggleHistory() {
    if (showHistory) { setShowHistory(false); return; }
    setHistLoading(true);
    setShowHistory(true);
    try {
      const res = await getKbHistory(activeSection, activeLang);
      setHistory(res.data.data || []);
    } catch (err) {
      setHistory([]);
      showToast('err', 'Failed to load version history. Please try again.');
      console.warn('KnowledgeBaseEditor: history fetch failed', err?.message);
    } finally {
      setHistLoading(false);
    }
  }

  // Show toast for 3 seconds then clear
  function showToast(type, message) {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  }

  async function handleSave() {
    if (editContent.trim().length === 0) {
      showToast('err', 'Content cannot be empty.');
      return;
    }
    if (editContent.length > MAX_CHARS) {
      showToast('err', `Content is ${editContent.length} chars — reduce to ${MAX_CHARS} or fewer.`);
      return;
    }
    setSaving(true);
    try {
      const res = await updateKbSection(activeSection, { content: editContent, language: activeLang });
      showToast('ok', res.data.message);
      // Refresh KB data so version + updatedBy reflect the save
      await loadKb();
    } catch (err) {
      const msg = err.response?.data?.message || 'Save failed. Please try again.';
      showToast('err', msg);
    } finally {
      setSaving(false);
    }
  }

  // Current record metadata for the active section + language
  const currentRecord  = (kbData[activeSection] || []).find(r => r.language === activeLang);
  const currentSection = SECTIONS.find(sec => sec.key === activeSection);
  const charCount      = editContent.length;
  const isOver         = charCount > MAX_CHARS;
  const canSave        = !saving && !isOver && editContent.trim().length > 0;

  return (
    <div>
      {toast && (
        <div style={{ ...s.toast, ...(toast.type === 'ok' ? s.toastOk : s.toastErr), marginBottom: '12px' }}>
          {toast.message}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '32px', textAlign: 'center', color: '#aaa', fontSize: '13px' }}>Loading knowledge base...</div>
      ) : (
        <div style={s.wrap}>
          {/* Section list — left sidebar */}
          <div style={s.sidebar}>
            <div style={s.sidebarHead}>Sections</div>
            {SECTIONS.map(sec => {
              const hasContent = (kbData[sec.key] || []).some(r => r.language === activeLang);
              return (
                <button key={sec.key} type="button"
                  onClick={() => setActiveSection(sec.key)}
                  style={{ ...s.sectionBtn, ...(activeSection === sec.key ? s.sectionActive : {}) }}>
                  {sec.label}
                  {!hasContent && (
                    <span style={{ marginLeft: '6px', fontSize: '9px', opacity: 0.6 }}>empty</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Editor panel — right */}
          <div style={s.editor}>
            <div style={s.editorHead}>
              <div style={s.title}>{currentSection?.label}</div>
              {/* Language toggle — switches which language variant is being edited */}
              <div style={s.langToggle}>
                {LANGUAGES.map(lang => (
                  <button key={lang.code} type="button"
                    onClick={() => setActiveLang(lang.code)}
                    style={{ ...s.langBtn, ...(activeLang === lang.code ? s.langActive : {}) }}>
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>

            <textarea
              style={{ ...s.textarea, borderColor: isOver ? '#e74c3c' : '#ddd' }}
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              placeholder={`Enter ${currentSection?.label} content in ${activeLang.toUpperCase()}. Plain text only — no HTML. Max ${MAX_CHARS} characters.`}
              rows={10}
            />

            <div style={s.charRow}>
              <span style={isOver ? s.charOver : s.charCount}>
                {charCount} / {MAX_CHARS} characters{isOver ? ' — over limit' : ''}
              </span>
              {currentRecord ? (
                <span style={s.meta}>
                  Version {currentRecord.version}
                  {currentRecord.updatedBy
                    ? ` · last saved by ${currentRecord.updatedBy.firstName} ${currentRecord.updatedBy.lastName}`
                    : ''}
                  {currentRecord.updatedAt
                    ? ` · ${new Date(currentRecord.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
                    : ''}
                </span>
              ) : (
                <span style={s.empty}>No {activeLang.toUpperCase()} content saved yet</span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0' }}>
              <button type="button" onClick={handleSave}
                disabled={!canSave}
                style={{ ...s.saveBtn, ...(!canSave ? s.saveBtnDis : {}) }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button type="button" onClick={handleToggleHistory} style={s.histBtn}>
                {showHistory ? 'Hide history' : 'Version history'}
              </button>
            </div>

            {showHistory && (
              <div style={s.histPanel}>
                <div style={s.histHead}>Prior versions</div>
                {histLoading ? (
                  <div style={s.histEmpty}>Loading...</div>
                ) : history.length === 0 ? (
                  <div style={s.histEmpty}>No prior versions saved yet.</div>
                ) : (
                  history.map((h) => (
                    <div key={h.version} style={s.histEntry}>
                      <div style={s.histMeta}>
                        v{h.version}
                        {h.savedBy ? ` · saved by ${h.savedBy.firstName} ${h.savedBy.lastName}` : ''}
                        {h.savedAt ? ` · ${new Date(h.savedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                      </div>
                      <div style={s.histContent}>{h.content}</div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseEditor;
